<template>
  <div>
    <v-sheet class="transparent-background-color">
      <v-card class="page-card">
        <v-card-title class="mx-3">
          {{
            $t(
              isNew
                ? "CRUD.newInfo"
                : savable
                ? "CRUD.editInfo"
                : "CRUD.viewInfo",
              [$t("Stock.ObjectName")]
            )
          }}
          <v-spacer></v-spacer>
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                @click="
                  $emit('mode', null);
                  $emit('input', null);
                "
                >fa-times
              </v-icon>
            </template>
            <span>
              {{ $t("CRUD.backToList", [$t("Stock.ObjectName")]) }}
            </span>
          </v-tooltip>
        </v-card-title>
        <v-progress-linear :indeterminate="loading"></v-progress-linear>
        <v-card-text>
          <ValidationObserver ref="observer">
            <v-container>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("Stock.Fields.stock_code") + $t("Global.colon") }}
                  <Required :show="savable" :mode="mode" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('Stock.Fields.stock_code')"
                    rules="required"
                  >
                    <v-text-field
                      single-line
                      v-model="stock.code"
                      :hint="isNew ? '' : $t('Stock.codeHint')"
                      :error-messages="
                        $resparse
                          .getErrorMsgs(serverErrors, 'code')
                          .concat(errors)
                      "
                    >
                      <template v-slot:message="{ key, message }">
                        <div :class="key == 0 ? 'danger--text' : ''">
                          {{ message }}
                        </div>
                      </template>
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ stock.code }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("Stock.Fields.stock_name") + $t("Global.colon") }}
                  <Required :show="savable" :mode="mode" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('Stock.Fields.stock_name')"
                    rules="required"
                  >
                    <v-text-field
                      single-line
                      v-model="stock.name"
                      :hint="isNew ? '' : $t('Stock.nameHint')"
                      :error-messages="
                        $resparse
                          .getErrorMsgs(serverErrors, 'name')
                          .concat(errors)
                      "
                    >
                      <template v-slot:message="{ key, message }">
                        <div :class="key == 0 ? 'danger--text' : ''">
                          {{ message }}
                        </div>
                      </template>
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ stock.name }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("Stock.Fields.stock_district") + $t("Global.colon") }}
                  <Required :show="savable" :mode="mode" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('Stock.Fields.stock_district')"
                    rules="required"
                  >
                    <SearcherStockDistrict
                      v-model="stock.stock_district"
                      no-label
                      unclearable
                      :error-messages="
                        $resparse
                          .getErrorMsgs(serverErrors, 'stock_district_code')
                          .concat(errors)
                      "
                    />
                  </ValidationProvider>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{
                    stock && stock.stock_district
                      ? stock.stock_district.name
                      : ""
                  }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("Stock.Fields.stock_manager") + $t("Global.colon") }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-text-field
                    single-line
                    v-model="stock.manager"
                    :hint="isNew ? '' : $t('Stock.managerHint')"
                  >
                    <template v-slot:message="{ key, message }">
                      <div :class="key == 0 ? 'danger--text' : ''">
                        {{ message }}
                      </div>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ stock.manager }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("Stock.Fields.tel") + $t("Global.colon") }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <ValidationProvider
                    v-slot="{ errors }"
                    :name="$t('Stock.Fields.tel')"
                    rules="mobile"
                  >
                    <v-text-field
                      single-line
                      v-model="stock.tel"
                      :hint="isNew ? '' : $t('Stock.telHint')"
                      :error-messages="errors"
                    >
                      <template v-slot:message="{ key, message }">
                        <div :class="key == 0 ? 'danger--text' : ''">
                          {{ message }}
                        </div>
                      </template>
                    </v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ stock.tel }}
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="3"
                  :class="
                    savable
                      ? 'font-weight-bold text-right mt-8'
                      : 'font-weight-bold text-right'
                  "
                >
                  {{ $t("Stock.Fields.stock_address") + $t("Global.colon") }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <!-- 三级地址 -->
                  <CitiesSelectGroup
                    :masterItem="stock"
                    @provinceSelected="stock.province_id = $event.id"
                    @citySelected="stock.city_id = $event.id"
                    @districtSelected="stock.area_id = $event.id"
                  />
                  <v-text-field single-line v-model="stock.address">
                    <template v-slot:message="{ key, message }">
                      <div :class="key == 0 ? 'danger--text' : ''">
                        {{ message }}
                      </div>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  <!-- 三级地址 -->
                  <AddressText
                    :masterItem="stock"
                    :detailAddress="stock.address"
                  />
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("Stock.Fields.memo") + $t("Global.colon") }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-text-field single-line v-model="stock.memo">
                    <template v-slot:message="{ key, message }">
                      <div :class="key == 0 ? 'danger--text' : ''">
                        {{ message }}
                      </div>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ stock.memo }}
                </v-col>
              </v-row>
              <v-row
                align="center"
                v-if="settings && settings.shop_third_account"
              >
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("Stock.Fields.third_ext_code") + $t("Global.colon") }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-text-field single-line v-model="stock.third_ext_code">
                    <template v-slot:message="{ key, message }">
                      <div :class="key == 0 ? 'danger--text' : ''">
                        {{ message }}
                      </div>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ stock.third_ext_code }}
                </v-col>
              </v-row>
              <v-row
                align="center"
                v-if="settings && settings.shop_third_account"
              >
                <v-col cols="3" class="font-weight-bold text-right">
                  {{
                    $t("Stock.Fields.third_stock_number") + $t("Global.colon")
                  }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-text-field single-line v-model="stock.third_stock_number">
                    <template v-slot:message="{ key, message }">
                      <div :class="key == 0 ? 'danger--text' : ''">
                        {{ message }}
                      </div>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ stock.third_stock_number }}
                </v-col>
              </v-row>
              <v-row
                align="center"
                v-if="
                  $permission.permissionAllow('Requisitions', 'addViews') &&
                  stock.type == 0 &&
                  !isNew
                "
              >
                <v-col cols="3" class="font-weight-bold text-right">
                  {{
                    $t("Stock.Fields.auto_requisition_from_stock") +
                    $t("Global.colon")
                  }}
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <SearcherStock
                    v-model="stocksSelected"
                    :remove-stock-ids="[stock.id]"
                    :stock-types="[0]"
                    no-label
                  />
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{
                    stock.auto_requisition_from_stock != 0 &&
                    stock.auto_requisition_from
                      ? stock.auto_requisition_from.name
                      : ""
                  }}
                </v-col>
              </v-row>
              <!-- 自动生成时间段 -->
              <v-row align="center" v-if="$permission.permissionAllow('Requisitions', 'addViews') &&
                  stock.type == 0 &&
                  !isNew &&
                  stock.auto_requisition_from_stock">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("PurchasePlans.autoRuler.auto_time") + $t("Global.colon") }}
                </v-col>
                <v-col cols="9" md="7">
                  <v-data-table
                    :headers="defaultHeaders"
                    :items="timesData"
                    multi-sort
                    class="elevation-1 px-4 pt-2"
                    disable-pagination
                    hide-default-header
                    hide-default-footer
                    fixed-header
                  >
                    <template v-slot:item.actions="{ item }">
                      <!-- 删除按钮 -->
                      <v-btn
                        v-if="savable"
                        small
                        text
                        class="danger--text pa-0"
                        @click="
                          timesData.splice(
                            timesData.findIndex(
                              (data) => data.value == item.value
                            ),
                            1
                          )
                        "
                      >
                        <v-icon>fa-times</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-col>
                <v-col cols="3"></v-col>
                <v-col cols="9" md="7">
                  <v-btn
                    v-if="savable"
                    color="primary"
                    class="my-1 mr-3"
                    x-small
                    outlined
                    @click="addDialog = true;addType = 4"
                  >
                    {{ $t("PurchasePlans.autoRuler.add_dialog_title") }}
                  </v-btn>
                </v-col>
              </v-row>
              <!-- 出库单时间范围类型 -->
              <v-row align="center" v-if="$permission.permissionAllow('Requisitions', 'addViews') &&
                  stock.type == 0 &&
                  !isNew &&
                  stock.auto_requisition_from_stock">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("PurchasePlans.autoRuler.date_type") + $t("Global.colon") }}
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-radio-group row v-model="date_type">
                    <v-radio
                      :label="$t('StockOrderOUT.Fields.order_time')"
                      :key="1"
                      :value="1"
                    />
                    <v-radio
                      :label="$t('StockOrderOUT.Fields.created_time')"
                      :key="2"
                      :value="2"
                    />
                    <v-radio
                      :label="$t('StockOrderOUT.Fields.actual_time')"
                      :key="3"
                      :value="3"
                    />
                    <v-radio
                      :label="$t('StockOrderOUT.Fields.expect_time')"
                      :key="4"
                      :value="4"
                    />
                  </v-radio-group>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{
                    date_type == 1 ?
                        $t('StockOrderOUT.Fields.order_time')
                        : date_type == 2 ?
                            $t('StockOrderOUT.Fields.created_time')
                            : date_type == 3 ?
                                $t('StockOrderOUT.Fields.actual_time')
                                : date_type == 4 ?
                                    $t('StockOrderOUT.Fields.expect_time')
                                    : ''
                  }}
                </v-col>
              </v-row>
              <!-- 出库单时间范围天数 -->
              <v-row align="center" v-if="$permission.permissionAllow('Requisitions', 'addViews') &&
                  stock.type == 0 &&
                  !isNew &&
                  stock.auto_requisition_from_stock">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("PurchasePlans.autoRuler.date_day") + $t("Global.colon") }}
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-text-field
                    v-model="date_day"
                    dense
                    single-line
                    :suffix="$t('PurchasePlans.autoRuler.daily')"
                    type="number"
                    style="max-width: 150px"
                  >
                  </v-text-field>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ date_day }} {{ $t("PurchasePlans.autoRuler.daily") }}
                </v-col>
              </v-row>
              <v-row align="center" v-if="$permission.permissionAllow('Requisitions', 'addViews') &&
                  stock.type == 0 &&
                  !isNew &&
                  stock.auto_requisition_from_stock">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("PurchasePlans.autoRuler.template") + $t("Global.colon") }}
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <SearcherTemplate
                    v-model="template"
                    no-label
                  />
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ stock.template_name }}
                </v-col>
              </v-row>
              <!-- 短信地址 -->
              <v-row align="center" v-if="$permission.permissionAllow('Requisitions', 'addViews') &&
                  stock.type == 0 &&
                  !isNew &&
                  stock.auto_requisition_from_stock">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("PurchasePlans.autoRuler.sms") + $t("Global.colon") }}
                </v-col>
                <v-col cols="9" md="7">
                  <v-data-table
                    :headers="defaultHeaders"
                    :items="smsData"
                    multi-sort
                    class="elevation-1 px-4 pt-2"
                    disable-pagination
                    hide-default-header
                    hide-default-footer
                    fixed-header
                  >
                    <template v-slot:item.actions="{ item }">
                      <!-- 删除按钮 -->
                      <v-btn
                        v-if="savable"
                        small
                        text
                        class="danger--text pa-0"
                        @click="
                          smsData.splice(
                            smsData.findIndex(
                              (data) => data.value == item.value
                            ),
                            1
                          )
                        "
                      >
                        <v-icon>fa-times</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-col>
                <v-col cols="3"></v-col>
                <v-col cols="9" md="7">
                  <v-btn
                    v-if="savable"
                    color="primary"
                    class="my-1 mr-3"
                    x-small
                    outlined
                    @click="addDialog = true;addType = 2"
                  >
                    {{ $t("PurchasePlans.autoRuler.add_dialog_title") }}
                  </v-btn>
                </v-col>
              </v-row>
              <!-- 邮件地址 -->
              <v-row align="center" v-if="$permission.permissionAllow('Requisitions', 'addViews') &&
                  stock.type == 0 &&
                  !isNew &&
                  stock.auto_requisition_from_stock">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("PurchasePlans.autoRuler.emails") + $t("Global.colon") }}
                </v-col>
                <v-col cols="9" md="7">
                  <v-data-table
                    :headers="defaultHeaders"
                    :items="emailData"
                    multi-sort
                    class="elevation-1 px-4 pt-2"
                    disable-pagination
                    hide-default-header
                    hide-default-footer
                    fixed-header
                  >
                    <template v-slot:item.actions="{ item }">
                      <!-- 删除按钮 -->
                      <v-btn
                        v-if="savable"
                        small
                        text
                        class="danger--text pa-0"
                        @click="
                          emailData.splice(
                            emailData.findIndex(
                              (data) => data.value == item.value
                            ),
                            1
                          )
                        "
                      >
                        <v-icon>fa-times</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-col>
                <v-col cols="3"></v-col>
                <v-col cols="9" md="7">
                  <v-btn
                    v-if="savable"
                    color="primary"
                    class="my-1 mr-3"
                    x-small
                    outlined
                    @click="addDialog = true;addType = 3"
                  >
                    {{ $t("PurchasePlans.autoRuler.add_dialog_title") }}
                  </v-btn>
                </v-col>
              </v-row>
              <v-row align="center" v-if="stock.type != 1">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{
                    $t("Stock.stock_take_type.ObjectName") + $t("Global.colon")
                  }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-radio-group row v-model="stock_take_type">
                    <v-radio
                      :label="$t('Stock.stock_take_type.typeOne')"
                      :key="1"
                      :value="1"
                    />
                    <v-radio
                      :label="$t('Stock.stock_take_type.typeTwo')"
                      :key="2"
                      :value="2"
                    />
                    <v-radio
                      :label="$t('Stock.stock_take_type.typeThree')"
                      :key="3"
                      :value="3"
                    />
                  </v-radio-group>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{
                    stock_take_type == 1
                      ? $t("Stock.stock_take_type.typeOne")
                      : stock_take_type == 2
                      ? $t("Stock.stock_take_type.typeTwo")
                      : stock_take_type == 3
                      ? $t("Stock.stock_take_type.typeThree")
                      : ""
                  }}
                </v-col>
              </v-row>
              <v-row
                align="center"
                v-if="(isView || !isNew) && stock.status != -1"
              >
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("Stock.Fields.status") + $t("Global.colon") }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-radio-group row v-model="stock.status">
                    <v-radio
                      :label="$t('Stock.status.on')"
                      :key="1"
                      :value="1"
                    />
                    <v-radio
                      :label="$t('Stock.status.off')"
                      :key="0"
                      :value="0"
                    />
                  </v-radio-group>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  <v-chip :color="stock.status | stockStatusColor" small label>
                    {{ stock.status | stockStatusText }}
                  </v-chip>
                </v-col>
              </v-row>
              <v-row
                align="center"
                v-if="
                  settings && settings.stock_position_setting && stock.type == 0
                "
              >
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("Stock.Fields.wms_setting") + $t("Global.colon") }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-checkbox
                    v-model="stock.wms_setting"
                    :label="$t('Stock.stockWMSSettings.on')"
                    :true-value="1"
                    :false-value="0"
                  ></v-checkbox>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  <v-chip
                    :color="stock.wms_setting ? 'primary' : ''"
                    small
                    label
                  >
                    {{
                      $t(
                        "Stock.stockWMSSettings." +
                          (stock.wms_setting ? "on" : "off")
                      )
                    }}
                  </v-chip>
                </v-col>
              </v-row>
              <v-row
                align="center"
                v-if="
                  settings && settings.material_position_bind && stock.type == 0
                "
              >
                <v-col cols="3" class="font-weight-bold text-right">
                  {{
                    $t("Stock.Fields.simple_wms_setting") + $t("Global.colon")
                  }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-checkbox
                    v-model="stock.simple_wms_setting"
                    :label="$t('Stock.stockWMSSettings.on')"
                    :true-value="1"
                    :false-value="0"
                  ></v-checkbox>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  <v-chip
                    :color="stock.simple_wms_setting ? 'primary' : ''"
                    small
                    label
                  >
                    {{
                      $t(
                        "Stock.stockWMSSettings." +
                          (stock.simple_wms_setting ? "on" : "off")
                      )
                    }}
                  </v-chip>
                </v-col>
              </v-row>
              <v-row
                v-if="settings && settings.production_module && stock.type == 0"
                align="center"
              >
                <v-col cols="3" class="font-weight-bold text-right">
                  {{
                    $t("Stock.Fields.production_attribute") + $t("Global.colon")
                  }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-checkbox
                    v-model="stock.production_attribute"
                    :label="$t('Stock.productionAttributeFlgs.on')"
                    :true-value="1"
                    :false-value="0"
                  ></v-checkbox>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  <v-chip
                    :color="stock.production_attribute ? 'primary' : ''"
                    small
                    label
                  >
                    {{
                      $t(
                        "Stock.productionAttributeFlgs." +
                          (stock.production_attribute ? "on" : "off")
                      )
                    }}
                  </v-chip>
                </v-col>
              </v-row>
              <v-row
                v-if="settings && settings.stock_area_enable"
                align="center"
              >
                <v-col cols="3" class="font-weight-bold text-right">
                  {{
                    $t("Stock.Fields.stock_area_setting") + $t("Global.colon")
                  }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-checkbox
                    v-model="stock.stock_area"
                    :label="$t('StockArea.status.on')"
                    :true-value="1"
                    :false-value="0"
                  ></v-checkbox>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  <v-chip
                    :color="stock.stock_area ? 'primary' : ''"
                    small
                    label
                  >
                    {{
                      $t(
                        "Stock.stockAreaSettings." +
                          (stock.stock_area ? "on" : "off")
                      )
                    }}
                  </v-chip>
                </v-col>
              </v-row>
              <v-row
                v-if="settings && settings.stock_roadway_setting"
                align="center"
              >
                <v-col cols="3" class="font-weight-bold text-right">
                  {{
                    $t("Stock.Fields.stock_roadway_setting") +
                    $t("Global.colon")
                  }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-checkbox
                    v-model="stock.stock_roadway_setting"
                    :label="$t('Stock.stockRoadwaySettings.on')"
                    :true-value="1"
                    :false-value="0"
                  ></v-checkbox>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  <v-chip
                    :color="stock.stock_roadway_setting ? 'primary' : ''"
                    small
                    label
                  >
                    {{
                      $t(
                        "Stock.stockRoadwaySettings." +
                          (stock.stock_roadway_setting ? "on" : "off")
                      )
                    }}
                  </v-chip>
                </v-col>
              </v-row>
              <v-row
                v-if="
                  settings &&
                  settings.stock_position_setting &&
                  stock.wms_setting
                "
              >
                <v-col
                  cols="3"
                  :class="
                    savable
                      ? 'font-weight-bold text-right mt-5'
                      : 'font-weight-bold text-right'
                  "
                >
                  {{
                    $t("Position.positionNameDisplaySetting") +
                    $t("Global.colon")
                  }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-checkbox
                    v-model="areaNameDisplayChecked"
                    :key="3"
                    :label="
                      $t('Position.positionNameDisplayType.stock_area_name')
                    "
                  ></v-checkbox>
                  <v-checkbox
                    v-model="roadwayCodeDisplayChecked"
                    :key="4"
                    :label="$t('Position.positionNameDisplayType.roadway_code')"
                  ></v-checkbox>
                  <v-select
                    v-model="positionNameDisplaySelected"
                    :items="positionNameDisplay"
                    item-value="value"
                    item-text="name"
                    :label="$t('Position.ObjectName')"
                  ></v-select>
                </v-col>
                <v-col cols="9" md="7" v-else> </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{
                    $t("Stock.material_sort.ObjectName") + $t("Global.colon")
                  }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-radio-group row v-model="material_sort">
                    <v-radio
                      :label="$t('Stock.material_sort.material_sort_one')"
                      :key="1"
                      :value="1"
                    />
                    <v-radio
                      :label="$t('Stock.material_sort.material_sort_two')"
                      :key="2"
                      :value="2"
                    />
                  </v-radio-group>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{
                    material_sort == 1
                      ? $t("Stock.material_sort.material_sort_one")
                      : material_sort == 2
                      ? $t("Stock.material_sort.material_sort_two")
                      : ""
                  }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("Stock.identity_type.ObjectName") + $t("Global.colon") }}
                  <Required :show="savable" />
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <v-radio-group row v-model="identity_type">
                    <v-radio
                      :label="$t('Stock.identity_type.selfOwned')"
                      :key="1"
                      :value="1"
                    />
                    <v-radio
                      :label="$t('Stock.identity_type.threeParty')"
                      :key="2"
                      :value="2"
                    />
                    <v-radio
                      :label="$t('Stock.identity_type.none')"
                      :key="0"
                      :value="0"
                    />
                  </v-radio-group>
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{
                    identity_type == 1
                        ? $t("Stock.identity_type.selfOwned")
                        : identity_type == 2
                            ? $t("Stock.identity_type.threeParty")
                            : ""
                  }}
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="3" class="font-weight-bold text-right">
                  {{ $t("Stock.Fields.requisition_bill_template") + $t("Global.colon") }}
                </v-col>
                <v-col cols="9" md="7" v-if="savable">
                  <SearcherTemplate
                    v-model="requisitionBillTemplate"
                    no-label
                    notDefault
                  />
                </v-col>
                <v-col cols="9" md="7" v-else>
                  {{ stock.requisition_bill_template_name }}
                </v-col>
              </v-row>
            </v-container>
          </ValidationObserver>
        </v-card-text>
      </v-card>
      <v-sheet class="page-opts">
        <v-card>
          <v-card-title>
            <v-row dense>
              <v-col cols="12">
                <v-btn
                  outlined
                  color="secondary"
                  class="elevation-1"
                  block
                  @click="
                    $emit('input', null);
                    $emit('mode', null);
                  "
                >
                  <v-icon small class="pe-1">fa-times</v-icon>
                  {{ $t("CRUD.backToList", [$t("Stock.ObjectName")]) }}
                </v-btn>
              </v-col>
              <v-col cols="12" v-if="isView">
                <v-btn
                  color="accent"
                  block
                  :disabled="loading && !stock.id"
                  @click="$emit('mode', 'edit')"
                >
                  <v-icon small class="pe-1">fa-pencil-alt</v-icon>
                  {{ $t("CRUD.editInfo", [$t("Stock.ObjectName")]) }}
                </v-btn>
              </v-col>
              <v-col cols="12" v-if="savable">
                <v-btn
                  color="success"
                  block
                  :disabled="loading && !stock.id"
                  @click="save()"
                >
                  <v-icon small class="pe-1">fa-save</v-icon>
                  {{ $t("CRUD.saveInfo", [$t("Stock.ObjectName")]) }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
        </v-card>
        <v-card class="mt-4" v-if="isView">
          <v-card-title>
            <v-list dense width="100%">
              <!-- 期初 -->
              <v-list-item v-if="stock.status == -1" color="primary">
                <v-btn
                  outlined
                  color="primary"
                  class="elevation-1"
                  block
                  :disabled="loading && !stock.id"
                  @click.stop="
                    $router.push({
                      path: '/stocks/initialize',
                      query: { stock_id: stock.id, mode: 'stockInitialize' },
                    })
                  "
                >
                  <v-icon color="primary" x-small>fa-hourglass-start</v-icon>
                  <span class="mx-2">
                    {{ $t("Stock.actions.initialize") }}
                  </span>
                </v-btn>
              </v-list-item>
              <!-- 仓库管理 -->
              <v-list-item
                v-if="
                  stock.type == 0 &&
                  $permission.permissionAllow('StockMaterials', 'index')
                "
              >
                <v-btn
                  outlined
                  color="primary"
                  class="elevation-1 my-1"
                  block
                  :disabled="loading && !stock.id"
                  @click.stop="
                    $router.push({
                      path: '/stocks/stock-materials',
                      query: { stock_id: stock.id },
                    })
                  "
                >
                  <span>{{ $t("Stock.actions.stockConfigure") }}</span>
                </v-btn>
              </v-list-item>
              <!-- 批量保存 -->
              <v-list-item
                v-if="
                  stock.type == 0 &&
                  $permission.permissionAllow(
                    'StockMaterials',
                    'singleBatchSave'
                  )
                "
              >
                <v-menu
                  v-model="btnMenu"
                  close-on-click
                  close-on-content-click
                  offset-y
                  offset-overflow
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      class="elevation-1 my-1"
                      block
                      :disabled="loading && !stock.id"
                      @click.stop=""
                      outlined
                      color="primary"
                    >
                      {{ $t("Stock.batchSave.title") }}
                      <v-icon x-small class="ps-1">
                        {{ btnMenu ? "fa-caret-up" : "fa-caret-down" }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item
                      @click.stop="
                        singleBatchSave(stock.id, { add_material: 1 })
                      "
                    >
                      <v-list-item-title>
                        {{ $t("Stock.batchSave.add_material") }}
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      @click.stop="singleBatchSave(stock.id, { status: 1 })"
                    >
                      <v-list-item-title>
                        {{ $t("Stock.batchSave.status_one") }}
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      @click.stop="singleBatchSave(stock.id, { status: 0 })"
                    >
                      <v-list-item-title>
                        {{ $t("Stock.batchSave.status_zero") }}
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      @click.stop="
                        singleBatchSave(stock.id, { is_negative_allowed: 1 })
                      "
                    >
                      <v-list-item-title>
                        {{ $t("Stock.batchSave.is_negative_allowed_one") }}
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      @click.stop="
                        singleBatchSave(stock.id, { is_negative_allowed: 0 })
                      "
                    >
                      <v-list-item-title>
                        {{ $t("Stock.batchSave.is_negative_allowed_zero") }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-list-item>
              <!-- 配送范围 -->
              <v-list-item
                v-if="
                  stock.status == 1 &&
                  $permission.permissionAllow(
                    'DistributionCenterConfigs',
                    'addDistrict'
                  )
                "
              >
                <v-btn
                  outlined
                  color="primary"
                  class="elevation-1 my-1"
                  block
                  :disabled="loading && !stock.id"
                  @click.stop="
                    $router.push({
                      path: '/stocks/distribution-coverage',
                      query: { stock_id: stock.id },
                    })
                  "
                >
                  <span>
                    {{ $t("Stock.actions.distributionCenterConfigure") }}
                  </span>
                </v-btn>
              </v-list-item>
              <!-- 线路管理 -->
              <v-list-item
                v-if="
                  stock.type == 0 &&
                  $permission.permissionAllow('DistributionUsers', 'add')
                "
              >
                <v-btn
                  outlined
                  color="primary"
                  class="elevation-1 my-1"
                  block
                  :disabled="loading && !stock.id"
                  @click.stop="
                    $router.push({
                      path: '/stocks/distribution-users',
                      query: { stock_id: stock.id },
                    })
                  "
                >
                  <span>{{ $t("Stock.actions.distributionPath") }}</span>
                </v-btn>
              </v-list-item>
              <!-- 配送点配置 -->
              <v-list-item
                v-if="
                  stock.status != -1 &&
                  $permission.permissionAllow('DeliveryPoints', 'bind') &&
                  settings &&
                  settings.delivery_point
                "
              >
                <v-btn
                  outlined
                  color="primary"
                  class="elevation-1 my-1"
                  block
                  :disabled="loading && !stock.id"
                  @click.stop="
                    $router.push({
                      path: '/stocks/delivery-point/setting',
                      query: { stock_id: stock.id },
                    })
                  "
                >
                  <span>
                    {{ $t("DeliveryPoint.settingBtn") }}
                  </span>
                </v-btn>
              </v-list-item>
              <!-- 快递设置 -->
              <v-list-item
                v-if="
                  stock.type == 0 &&
                  $permission.permissionAllow('Stocks', 'kdnSettings')
                "
              >
                <v-btn
                  outlined
                  color="primary"
                  class="elevation-1 my-1"
                  block
                  :disabled="loading && !stock.id"
                  @click.stop="
                    $router.push({
                      path: '/stocks/stock-logistic-setting',
                      query: { stock_id: stock.id },
                    })
                  "
                >
                  <span>{{ $t("Stock.actions.logisticSetting") }}</span>
                </v-btn>
              </v-list-item>
              <!-- 分拣出库 -->
              <v-list-item
                v-if="
                  $permission.permissionAllow(
                    'StockOuts',
                    'sortingOutSetting'
                  ) && stock.type != 1
                "
              >
                <v-btn
                  outlined
                  color="primary"
                  class="elevation-1 my-1"
                  block
                  :disabled="loading && !stock.id"
                  @click.stop="
                    $router.push({
                      path: '/stocks/sorting-out-setting',
                      query: { stock_id: stock.id },
                    })
                  "
                >
                  <span>{{ $t("Stock.actions.waveSortSetting") }}</span>
                </v-btn>
              </v-list-item>
              <!-- 库区管理 -->
              <v-list-item
                v-if="
                  settings &&
                  stock.type == 0 &&
                  $permission.permissionAllow('StockAreas', 'index') &&
                  ((settings.stock_position_setting && wmsSetting) ||
                    (settings.material_position_bind && simpleWmsSetting))
                "
              >
                <v-btn
                  outlined
                  color="primary"
                  class="elevation-1 my-1"
                  block
                  :disabled="loading && !stock.id"
                  @click.stop="
                    $router.push({
                      path: '/stocks/areas',
                      query: { stock_id: stock.id },
                    })
                  "
                >
                  <span>{{ $t("Stock.actions.stockArea") }}</span>
                </v-btn>
              </v-list-item>
              <!-- 巷道 -->
              <v-list-item
                v-if="
                  settings &&
                  stock.type == 0 &&
                  $permission.permissionAllow('StockRoadways', 'index') &&
                  ((settings.stock_position_setting && wmsSetting) ||
                    (settings.material_position_bind && simpleWmsSetting))
                "
              >
                <v-btn
                  outlined
                  color="primary"
                  class="elevation-1 my-1"
                  block
                  :disabled="loading && !stock.id"
                  @click.stop="
                    $router.push({
                      path: '/stocks/roadways',
                      query: { stock_id: stock.id },
                    })
                  "
                >
                  <span>{{ $t("Stock.actions.stockRoadway") }}</span>
                </v-btn>
              </v-list-item>
              <!-- 库位标签 -->
              <v-list-item
                v-if="
                  settings &&
                  stock.type == 0 &&
                  $permission.permissionAllow('StockPositionLabels', 'index') &&
                  ((settings.stock_position_setting && wmsSetting) ||
                    (settings.material_position_bind && simpleWmsSetting))
                "
              >
                <v-btn
                  outlined
                  color="primary"
                  class="elevation-1 my-1"
                  block
                  :disabled="loading && !stock.id"
                  @click.stop="
                    $router.replace(
                      '/stocks/position-labels?stock_id=' + stock.id
                    )
                  "
                >
                  <span>{{ $t("Stock.actions.positionLabel") }}</span>
                </v-btn>
              </v-list-item>
              <!-- 库位 -->
              <v-list-item
                v-if="
                  settings &&
                  stock.type == 0 &&
                  $permission.permissionAllow('StockPositions', 'index') &&
                  ((settings.stock_position_setting && wmsSetting) ||
                    (settings.material_position_bind && simpleWmsSetting))
                "
              >
                <v-btn
                  outlined
                  color="primary"
                  class="elevation-1 my-1"
                  block
                  :disabled="loading && !stock.id"
                  @click.stop="
                    $router.push({
                      path: '/stocks/positions',
                      query: { stock_id: stock.id },
                    })
                  "
                >
                  <span>{{ $t("Stock.actions.stockPosition") }}</span>
                </v-btn>
              </v-list-item>
              <!-- 容器 -->
              <v-list-item
                v-if="
                  settings &&
                  stock.type == 0 &&
                  $permission.permissionAllow('Containers', 'index') &&
                  ((settings.stock_position_setting && wmsSetting) ||
                    (settings.material_position_bind && simpleWmsSetting))
                "
              >
                <v-btn
                  outlined
                  color="primary"
                  class="elevation-1 my-1"
                  block
                  :disabled="loading && !stock.id"
                  @click.stop="
                    $router.push({
                      path: '/stocks/stock-containers',
                      query: { stock_id: stock.id },
                    })
                  "
                >
                  <span>{{ $t("Stock.actions.container") }}</span>
                </v-btn>
              </v-list-item>
              <!-- 波次 -->
              <v-list-item
                v-if="
                  settings &&
                  stock.type == 0 &&
                  $permission.permissionAllow('StockMaterials', 'index') &&
                  ((settings.stock_position_setting && wmsSetting) ||
                    (settings.material_position_bind && simpleWmsSetting))
                "
              >
                <v-btn
                  outlined
                  color="primary"
                  class="elevation-1 my-1"
                  block
                  :disabled="loading && !stock.id"
                  @click.stop="
                    $router.push({
                      path: '/stocks/wave-rulers',
                      query: { stock_id: stock.id },
                    })
                  "
                >
                  <span>{{ $t("Stock.actions.waveSetting") }}</span>
                </v-btn>
              </v-list-item>
              <!-- 采购任务设置 -->
              <v-list-item
                v-if="
                  stock &&
                  stock.type == 0 &&
                  $permission.permissionAllow('PurchaseUserTasks', 'index')
                "
              >
                <v-btn
                  outlined
                  color="primary"
                  class="elevation-1 my-1"
                  block
                  :disabled="loading && !stock.id"
                  @click.stop="
                    $router.push({
                      path: '/stocks/purchase-task',
                      query: { stock_id: stock.id },
                    })
                  "
                >
                  <span>{{ $t("Stock.actions.purchaseTask") }}</span>
                </v-btn>
              </v-list-item>
            </v-list>
          </v-card-title>
        </v-card>
      </v-sheet>
    </v-sheet>
    <AddDialog
      v-model="addDialog"
      :type="addType"
      @add="
        addDialog = false;
        addData($event)
      "
    />
  </div>
</template>

<script>
import i18n from "@/i18n";
import { mapState } from "vuex";
import Consts from "@/consts.js";
import Required from "@/components/plugins/Required";
import Stocks from "@/api/stocks";
import SearcherStockDistrict from "@/components/plugins/SearcherStockDistrict";
import SearcherTemplate from "@/components/plugins/SearcherTemplate";
import SearcherStock from "@/components/plugins/SearcherStock";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import CitiesSelectGroup from "@/components/plugins/CitiesSelects";
import AddressText from "@/components/plugins/AddressText";
import StockMaterials from "@/api/stock_materials";
import AddDialog from "@/components/purchasePlans/plugins/AddDialog";

export default {
  name: "StockDetail",

  props: ["value", "mode"],

  components: {
    AddDialog,
    Required,
    SearcherStockDistrict,
    SearcherTemplate,
    SearcherStock,
    ValidationObserver,
    ValidationProvider,
    CitiesSelectGroup,
    AddressText,
  },

  computed: {
    isView() {
      return !this.mode || this.mode == "view";
    },
    savable() {
      return this.mode == "edit" || this.mode == "new";
    },
    isNew() {
      return this.mode == "new";
    },
    status() {
      return Consts.stock.status;
    },
    defaultHeaders: () => [
      {
        text: "",
        align: "start",
        sortable: false,
        value: "value",
      },
      {
        text: i18n.t("TableHeaders.actions"),
        align: "end",
        sortable: false,
        value: "actions",
      }
    ],
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "settings"]),
  },

  data: () => ({
    serverErrors: [],
    stock: {},
    wmsSetting: 0,
    simpleWmsSetting: 0,
    areaNameDisplayChecked: false,
    roadwayCodeDisplayChecked: false,
    positionNameDisplay: [
      {
        value: 1,
        name: i18n.t("Position.positionNameDisplayType.position_code_and_name"),
      },
      {
        value: 2,
        name: i18n.t("Position.positionNameDisplayType.position_name"),
      },
    ],
    positionNameDisplaySelected: 1,
    stocksSelected: {},
    stock_take_type: 1,
    identity_type: 0,
    btnMenu: false,
    material_sort: 1,
    addDialog: false,
    timesData: [],
    smsData: [],
    emailData: [],
    template: null,
    date_type: null,
    date_day: null,
    addType: null,
    requisitionBillTemplate: null
  }),

  methods: {
    save() {
      this.$store.dispatch("fullscreenLoading");
      this.serverErrors = []; //重置远端错误提示
      this.$refs.observer.validate().then(
        function (result) {
          if (result) {
            var stock = JSON.parse(JSON.stringify(this.stock));
            if (this.stock.stock_district && this.stock.stock_district.code) {
              stock.stock_district_code = this.stock.stock_district.code;
            }
            delete stock.stock_district;
            if (this.stock_take_type == 1 || this.stock_take_type == 2) {
              stock.stock_take_type = [this.stock_take_type];
            } else {
              stock.stock_take_type = [1, 2];
            }
            if (this.stocksSelected) {
             let auto_requisit_setting = {
                time_type: 1,
                times: this.timesData.map((data) => {return data.value}),
                template_id: (this.template) ? this.template.id : null,
                date_type: this.date_type,
                date_day: this.date_day,
                sms: this.smsData.map((data) => {return data.value}),
                emails: this.emailData.map((data) => {return data.value}),
              }
              stock.auto_requisit_setting = auto_requisit_setting;
            }
            if (this.requisitionBillTemplate) {
              stock.requisition_bill_template = this.requisitionBillTemplate.id
            }else {
              stock.requisition_bill_template = null;
            }
            stock.material_sort = this.material_sort;
            var position_name_display = [];
            if (this.areaNameDisplayChecked) position_name_display.push(3);
            if (this.roadwayCodeDisplayChecked) position_name_display.push(4);
            position_name_display.push(this.positionNameDisplaySelected);
            stock.position_name_display = position_name_display;
            stock.identity_type = this.identity_type;
            if (this.identity_type == 0) {
              stock.identity_type = null;
            }

            //校验成功再提交
            Stocks.save(stock.id, stock)
              .then(
                function (response) {
                  if (response.data.status.isOK()) {
                    this.$emit("save", this.stock);
                    this.$emit("mode", null);
                    this.$emit("input", null);
                  } else if (
                    response.data.error &&
                    response.data.error.data &&
                    "ERR333" == response.data.error.code
                  ) {
                    this.serverErrors = response.data.error.data;
                  }
                  this.$store.dispatch("fullscreenLoadingStop");
                }.bind(this)
              )
              .catch(
                function () {
                  this.$store.dispatch("fullscreenLoadingStop");
                }.bind(this)
              );
          } else {
            this.$store.dispatch("fullscreenLoadingStop");
          }
        }.bind(this)
      );
    },
    singleBatchSave(id, data) {
      StockMaterials.singleBatchSave(id, data);
    },
    addData(data) {
      if (!data) {
        return
      }
      if (this.addType == 4) {
        // 自动生成时间段
        // 检测数据是否存在
        if (this.timesData.find((value) => value.value == data)) {
          return;
        }
        this.timesData.push({
          value:data
        })
      }
      if (this.addType == 2) {
        // 短信
        // 检测数据是否存在
        if (this.smsData.find((value) => value.value == data)) {
          return;
        }
        this.smsData.push({
          value:data
        })
      }
      if (this.addType == 3) {
        // 邮件
        // 检测数据是否存在
        if (this.emailData.find((value) => value.value == data)) {
          return;
        }
        this.emailData.push({
          value:data
        })
      }
    },
  },

  watch: {
    value: {
      immediate: true, //初始赋值也调用
      handler() {
        this.timesData = [];
        this.smsData = [];
        this.emailData = [];
        this.template = null;
        this.date_type = null;
        this.date_day = null;
        this.serverErrors = []; //重置远端错误提示
        this.stock_take_type = 1;
        this.material_sort = 1;
        this.identity_type = 0;
        this.btnMenu = false;
        this.requisitionBillTemplate = null;
        if (this.value && this.value.id) {
          Stocks.fetchOne(this.value.id).then(
            function (response) {
              if (response.data.status.isOK()) {
                this.stock = response.data.stock;
                this.stocksSelected =
                  this.stock.auto_requisition_from_stock != 0
                    ? { id: this.stock.auto_requisition_from_stock }
                    : {};
                //获取wms设置
                this.wmsSetting = this.stock.wms_setting;
                this.simpleWmsSetting = this.stock.simple_wms_setting;
                //获取库位名称设置
                if (this.stock.position_name_display) {
                  if (this.stock.position_name_display.indexOf(1) > -1)
                    this.positionNameDisplaySelected = 1;
                  if (this.stock.position_name_display.indexOf(2) > -1)
                    this.positionNameDisplaySelected = 2;
                  if (this.stock.position_name_display.indexOf(3) > -1)
                    this.areaNameDisplayChecked = true;
                  if (this.stock.position_name_display.indexOf(4) > -1)
                    this.roadwayCodeDisplayChecked = true;
                }
                this.stock_take_type =
                  this.stock &&
                  this.stock.stock_take_type &&
                  this.stock.stock_take_type.length
                    ? this.stock.stock_take_type.length == 1
                      ? this.stock.stock_take_type[0] == 1
                        ? 1
                        : this.stock.stock_take_type[0] == 2
                        ? 2
                        : this.stock.stock_take_type.length == 2
                      : this.stock.stock_take_type.length == 2
                      ? 3
                      : ""
                    : "";
                this.material_sort = this.stock.material_sort;
                //未期初的仓库状态默认为启用
                // if (this.stock.status == -1) {
                //   this.stock.status = 1;
                // }
                if (this.stock.auto_requisit_setting) {
                  this.date_type = this.stock.auto_requisit_setting.date_type
                  this.date_day = this.stock.auto_requisit_setting.date_day
                  this.template = {id: this.stock.auto_requisit_setting.template_id}
                  this.stock.auto_requisit_setting.times.map((time) => {
                    this.timesData.push({value: time})
                  })
                  this.stock.auto_requisit_setting.sms.map((time) => {
                    this.smsData.push({value: time})
                  })
                  this.stock.auto_requisit_setting.emails.map((time) => {
                    this.emailData.push({value: time})
                  })
                }
                if (this.stock.requisition_bill_template) {
                  this.requisitionBillTemplate = {id: this.stock.requisition_bill_template}
                }
                if (
                  this.stock.identity_type == null ||
                  typeof this.stock.identity_type == "undefined" ||
                  this.stock.identity_type == undefined
                ) {
                  this.identity_type = 0;
                } else {
                  this.identity_type = this.stock.identity_type
                }
              }
            }.bind(this)
          );
        } else {
          // 新建仓库，初始化默认值
          this.stock = {
            status: -1,
            auto_requisition_from_stock: 0,
          };
          this.stocksSelected = {};
        }
        this.$scrollTop(); //返回顶部
      },
    },
    stocksSelected: {
      handler() {
        this.stock.auto_requisition_from_stock = this.stocksSelected
          ? this.stocksSelected.id
          : 0;
        if (!this.stocksSelected) {
          this.timesData = [];
          this.smsData = [];
          this.emailData = [];
          this.date_type = null;
          this.date_day = null;
        }
      },
    },
  },
};
</script>